const API_BASE = import.meta.env.VITE_API_BASE ?? '/api'
/** Persist across mobile Safari tab discards / pull-to-refresh (sessionStorage is too fragile). */
const GATE_TOKEN_KEY = 'redevicer_site_gate_token'

export type SiteGateStatus = {
  enabled: boolean
  unlocked: boolean
}

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function storageSet(key: string, value: string | null): void {
  try {
    if (value) {
      localStorage.setItem(key, value)
      // Migrate away from sessionStorage so iOS tab discard does not drop access.
      sessionStorage.removeItem(key)
    } else {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    }
  } catch {
    /* ignore */
  }
}

export function getSiteGateToken(): string | null {
  return storageGet(GATE_TOKEN_KEY)
}

function setSiteGateToken(token: string | null): void {
  storageSet(GATE_TOKEN_KEY, token)
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { detail?: string }).detail || res.statusText)
  }
  return res.json() as Promise<T>
}

function gateHeaders(): HeadersInit {
  const headers: Record<string, string> = {}
  const token = getSiteGateToken()
  if (token) headers['X-Site-Gate-Token'] = token
  return headers
}

async function fetchWithTimeout(input: string, init: RequestInit = {}, ms = 8000): Promise<Response> {
  const ctrl = new AbortController()
  const timer = window.setTimeout(() => ctrl.abort(), ms)
  try {
    return await fetch(input, { ...init, signal: ctrl.signal })
  } finally {
    window.clearTimeout(timer)
  }
}

export async function fetchSiteGateStatus(): Promise<SiteGateStatus> {
  const res = await fetchWithTimeout(`${API_BASE}/site-gate/status`, {
    credentials: 'include',
    headers: gateHeaders(),
  })
  return parseJson<SiteGateStatus>(res)
}

export async function unlockSiteGate(password: string): Promise<void> {
  const res = await fetchWithTimeout(
    `${API_BASE}/site-gate/unlock`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...gateHeaders() },
      body: JSON.stringify({ password: password.trim() }),
    },
    12000,
  )
  const data = await parseJson<{ token?: string | null }>(res)
  if (data.token) setSiteGateToken(data.token)
}

/** Clear gate cookie + session token (e.g. explicit logout from private preview). */
export async function clearSiteGateCookie(): Promise<void> {
  setSiteGateToken(null)
  await fetch(`${API_BASE}/site-gate/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => {})
}
